import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaSync, FaArrowLeft, FaCloudUploadAlt, FaUpload,
  FaMoneyBillWave, FaFileContract, FaChartLine, FaShieldAlt, FaTasks,
} from "react-icons/fa";
import "./MietverwaltungPage.css";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import Tab1Zahlungen from "./mietverwaltung/Tab1Zahlungen";
import Tab2Vertraege from "./mietverwaltung/Tab2Vertraege";
import Tab3Miethistorie from "./mietverwaltung/Tab3Miethistorie";
import Tab4Schaeden from "./mietverwaltung/Tab4Schaeden";
import Tab5Bescheinigungen from "./mietverwaltung/Tab5Bescheinigungen";
import Tab6Vorgaenge from "./mietverwaltung/Tab6Vorgaenge";

const API = API_BASE;

// Welle 101: jeder Tab hat seine eigene Freigabe (wie die Kacheln auf der Startseite)
const TABS = [
  { key: "zahlungen", label: "Zahlungen", Icon: FaMoneyBillWave, modul: "mietverwaltung.zahlungen" },
  { key: "vertraege", label: "Objekte & Verträge", Icon: FaFileContract, modul: "mietverwaltung.vertraege" },
  { key: "historie", label: "Miethistorie", Icon: FaChartLine, modul: "mietverwaltung.historie" },
  { key: "schaeden", label: "Schäden", Icon: FaShieldAlt, modul: "mietverwaltung.schaeden" },
  { key: "bescheinigungen", label: "Dokumente", Icon: FaFileContract, modul: "mietverwaltung.dokumente" },
  { key: "vorgaenge", label: "Vorgänge", Icon: FaTasks, modul: "mietverwaltung.vorgaenge" },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
function MietverwaltungPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef(null);
  const { user } = useAuth();
  // leere Modul-Liste = alles erlaubt (wie HomePage/Navbar)
  const darf = (modul) => !user || !user.module || user.module.length === 0 || user.module.includes(modul);
  const erlaubteTabs = TABS.filter((t) => darf(t.modul));

  // ─ View State: from URL query or null ─
  const [activeView, setActiveView] = useState(() => {
    const tab = searchParams.get("tab");
    if (["zahlungen", "vertraege", "historie", "schaeden", "bescheinigungen", "vorgaenge"].includes(tab)) return tab;
    return "zahlungen";
  });

  // Tab ohne Freigabe (z. B. per Link aufgerufen) → erster freigegebener Tab
  useEffect(() => {
    if (erlaubteTabs.length && !erlaubteTabs.some((t) => t.key === activeView)) {
      setActiveView(erlaubteTabs[0].key);
    }
  }, [activeView, erlaubteTabs]);

  // URL-Query-Änderungen (z. B. Drawer-Button „Mietbescheinigung") übernehmen
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["zahlungen", "vertraege", "historie", "schaeden", "bescheinigungen", "vorgaenge"].includes(tab)) {
      setActiveView(tab);
    }
  }, [searchParams]);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [dragover, setDragover] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [status, setStatus] = useState(null);

  // ─ Upload ─
  const handleUpload = async (mode) => {
    if (!selectedFile) return;
    setUploading(true);
    setStatus(null);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const endpoint = mode === "migrate" ? "migrate" : "sync";
      const res = await fetch(`${API}/mietverwaltung/api/${endpoint}`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.status === "ok") {
        const msg = mode === "migrate"
          ? `✅ Migration: ${data.inserted} Zeilen eingefügt`
          : `✅ Sync: ${data.deleted} gelöscht, ${data.inserted} neu eingefügt`;
        setStatus({ type: "success", text: msg });
        setSelectedFile(null);
        setShowUpload(false);
      } else {
        setStatus({ type: "error", text: `❌ Fehler: ${data.detail}` });
      }
    } catch (e) {
      setStatus({ type: "error", text: `❌ Netzwerkfehler: ${e.message}` });
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setSelectedFile(f);
  };

  return (
    <div className="mietverwaltung-page">
      {/* ── Header ── */}
      <div className="me-header">
        <div>
          <button className="me-btn me-btn-back" onClick={() => navigate("/")}>
            <FaArrowLeft /> Zurück
          </button>
          <h2>Mietverwaltung</h2>
        </div>
        <div className="me-header-actions">
          <button className="me-btn me-btn-secondary" onClick={() => setShowUpload((v) => !v)}>
            <FaUpload /> Upload
          </button>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      {activeView && (
        <div className="me-tabs">
          {erlaubteTabs.map(({ key, label, Icon }) => (
            <button key={key} className={`me-tab ${activeView === key ? "active" : ""}`}
              onClick={() => setActiveView(key)}>
              <Icon /> {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Status ── */}
      {status && <div className={`me-status ${status.type}`}>{status.text}</div>}

      {/* ── Upload ── */}
      <div className={`me-upload-area ${showUpload ? "visible" : ""}`}>
        <div
          className={`me-dropzone ${dragover ? "dragover" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
          onDragLeave={() => setDragover(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          <FaCloudUploadAlt className="icon" />
          <p>{selectedFile ? `📄 ${selectedFile.name}` : "Excel-Datei hierher ziehen oder klicken"}</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" style={{ display: "none" }}
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
        </div>
        {selectedFile && (
          <div className="me-upload-actions">
            <button className="me-btn me-btn-primary" disabled={uploading} onClick={() => handleUpload("sync")}>
              <FaSync /> {uploading ? "Läuft…" : "daten_mz aktualisieren"}
            </button>
            <span style={{ fontSize: "0.8em", color: "#94a3b8" }}>Löscht alte Einträge in daten_mz und importiert neu</span>
          </div>
        )}
      </div>

      {/* ═══════ VIEW 1: Zahlungen ═══════ */}
      {/* ═══════ VIEW 1: Zahlungen (Smart) ═══════ */}
      {activeView === "zahlungen" && <Tab1Zahlungen />}

      {/* ═══════ VIEW 2: Objekte & Verträge ═══════ */}
      {activeView === "vertraege" && <Tab2Vertraege />}

      {/* ═══════ VIEW 3: Miethistorie ═══════ */}
      {activeView === "historie" && <Tab3Miethistorie />}

      {/* ═══════ VIEW 4: Versicherungsschäden ═══════ */}
      {activeView === "schaeden" && <Tab4Schaeden />}

      {/* ═══════ VIEW 5: Mietbescheinigungen (Wohngeld) ═══════ */}
      {activeView === "bescheinigungen" && (
        <Tab5Bescheinigungen initialVertrag={searchParams.get("vertrag") || ""}
          initialDoc={searchParams.get("doc") || ""} />
      )}

      {/* ═══════ VIEW 6: Kündigungsvorgänge (Welle 81) ═══════ */}
      {activeView === "vorgaenge" && (
        <Tab6Vorgaenge initialVorgang={searchParams.get("vorgang") || ""} />
      )}

    </div>
  );
}

export default MietverwaltungPage;
